import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cityData from "../../utils/citydata";
import "./createBusinessProfile.css";
import { trackCreateBusinessStep } from "../../utils/analytics";

const START_YEAR = 1960;
const METRO_CITY_PRIORITY = [
  "Mumbai",
  "Delhi",
  "Bengaluru",
  "Bangalore",
  "Hyderabad",
  "Chennai",
  "Kolkata",
  "Pune",
  "Ahmedabad",
];
function CreateBusinessProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const progress = 100;
  const [businessName, setBusinessName] = useState("");
  const [inBusinessSince, setInBusinessSince] = useState("");
  const [basedIn, setBasedIn] = useState("");
  const [isContinuing, setIsContinuing] = useState(false);
  const [yearQuery, setYearQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= START_YEAR; year -= 1) {
      years.push(String(year));
    }
    return years;
  }, []);

  const cityOptions = useMemo(() => {
    const uniqueCities = new Set();
    cityData.forEach((entry) => {
      const city = entry?.City ? String(entry.City).trim() : "";
      if (city) uniqueCities.add(city);
    });

    const cityList = Array.from(uniqueCities);
    const metroSet = new Set(METRO_CITY_PRIORITY.map((city) => city.toLowerCase()));
    const metroCities = cityList
      .filter((city) => metroSet.has(city.toLowerCase()))
      .sort((a, b) => {
        const aIndex = METRO_CITY_PRIORITY.findIndex(
          (priorityCity) => priorityCity.toLowerCase() === a.toLowerCase(),
        );
        const bIndex = METRO_CITY_PRIORITY.findIndex(
          (priorityCity) => priorityCity.toLowerCase() === b.toLowerCase(),
        );
        return aIndex - bIndex;
      });
    const otherCities = cityList
      .filter((city) => !metroSet.has(city.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));

    return [...metroCities, ...otherCities];
  }, []);

  useEffect(() => {
    setYearQuery(inBusinessSince || "");
  }, [inBusinessSince]);

  useEffect(() => {
    setCityQuery(basedIn || "");
  }, [basedIn]);

  const filteredYearOptions = useMemo(() => {
    const q = yearQuery.trim().toLowerCase();
    const matchedYears = !q
      ? yearOptions
      : yearOptions.filter((year) => year.toLowerCase().includes(q));
    return matchedYears.filter((year) => year !== inBusinessSince);
  }, [inBusinessSince, yearOptions, yearQuery]);

  const filteredCityOptions = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    const matchedCities = !q
      ? cityOptions
      : cityOptions.filter((city) => city.toLowerCase().includes(q));
    return matchedCities.filter((city) => city !== basedIn);
  }, [basedIn, cityOptions, cityQuery]);

  const resolveExactOptionMatch = (options, rawValue) => {
    const normalizedValue = rawValue.trim().toLowerCase();
    if (!normalizedValue) return "";
    return options.find((option) => option.toLowerCase() === normalizedValue) || "";
  };

  const canContinue =
    businessName.trim().length > 0 && Boolean(inBusinessSince) && Boolean(basedIn);

  const handleCreateUser = async () => {
    if (!canContinue || isContinuing) return;

    trackCreateBusinessStep({ step: 3, buttonName: "Continue to verification" });

    const categoryId = location?.state?.categoryId || "";
    const categoryName = location?.state?.categoryName || "";
    const isOthersCategory = Boolean(location?.state?.isOthersCategory);
    const otherServiceName = String(location?.state?.otherServiceName || "").trim();
    const firstName = businessName.trim();
    const createUserDraft = {
      firstName,
      accountType: "professional",
      businessName: firstName,
      location: basedIn,
      activeSince: inBusinessSince,
      categoryId,
      professionalCategory: categoryName,
      professionalType: categoryName,
      category: categoryName,
      // Custom category name the user typed after picking "Others" — kept
      // separate from `category` so the real "Others" category is still
      // recorded. Same field the mobile app sends (allowlisted server-side,
      // no endpoint change).
      ...(isOthersCategory && otherServiceName ? { otherServices: otherServiceName } : {}),
      source: "WEB",
    };

    setIsContinuing(true);
    navigate("/create-business", {
      state: {
        createUserDraft,
        categoryId,
        categoryName,
      },
    });
  };

  return (
    <div className="create-business-profile-page">
      <div className="create-business-profile-shell">
        <main className="create-business-profile-main">
          <div className="create-business-profile-inline-header">
            <button
              type="button"
              className="create-business-profile-back-btn-icon"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <img src="/caretLeft.svg" alt="" />
            </button>
            <p>Tell us about you</p>
          </div>

          <h1 className="create-business-profile-title">Set up your business profile</h1>
          <p className="create-business-profile-subtitle">
            This is what homeowners will see when they discover you
          </p>

          <div className="create-business-profile-field">
            <label htmlFor="business-name">
              Business Name <span>*</span>
            </label>
            <input
              id="business-name"
              type="text"
              placeholder="Enter your business name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              onInput={(event) => setBusinessName(event.currentTarget.value)}
            />
          </div>

          <div className="create-business-profile-field">
            <label htmlFor="in-business-since-input">
              In Business Since <span>*</span>
            </label>
            <div
              className={`create-business-profile-dropdown-field ${
                isYearOpen ? "is-open" : ""
              }`}
            >
              <div className="create-business-profile-dropdown-input-row">
                <input
                  id="in-business-since-input"
                  type="text"
                  className={`create-business-profile-dropdown-input ${
                    !yearQuery ? "is-empty" : ""
                  }`}
                  value={yearQuery}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setYearQuery(nextValue);
                    setInBusinessSince(resolveExactOptionMatch(yearOptions, nextValue));
                    setIsYearOpen(true);
                    setIsCityOpen(false);
                  }}
                  onFocus={() => {
                    setIsYearOpen(true);
                    setIsCityOpen(false);
                  }}
                  placeholder="Select year"
                />
                <button
                  type="button"
                  className={`create-business-profile-dropdown-caret-btn ${
                    isYearOpen ? "is-open" : ""
                  }`}
                  onClick={() => {
                    setIsYearOpen((prev) => !prev);
                    setIsCityOpen(false);
                    if (isYearOpen) {
                      setYearQuery(inBusinessSince || "");
                    }
                  }}
                  aria-label={isYearOpen ? "Close year options" : "Open year options"}
                >
                  <img
                    src="/CaretRight.svg"
                    alt=""
                    className="create-business-profile-dropdown-caret"
                  />
                </button>
              </div>
              {isYearOpen ? (
                <div className="create-business-profile-dropdown-list">
                  {filteredYearOptions.length > 0 ? (
                    filteredYearOptions.map((year) => (
                      <button
                        key={year}
                        type="button"
                        className={`create-business-profile-dropdown-option ${
                          inBusinessSince === year ? "is-selected" : ""
                        }`}
                        onClick={() => {
                          setInBusinessSince(year);
                          setYearQuery(year);
                          setIsYearOpen(false);
                        }}
                      >
                        {year}
                      </button>
                    ))
                  ) : (
                    <div className="create-business-profile-dropdown-empty">
                      No years found
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="create-business-profile-field">
            <label htmlFor="based-in-input">
              Based In <span>*</span>
            </label>
            <div
              className={`create-business-profile-dropdown-field ${
                isCityOpen ? "is-open" : ""
              }`}
            >
              <div className="create-business-profile-dropdown-input-row">
                <input
                  id="based-in-input"
                  type="text"
                  className={`create-business-profile-dropdown-input ${
                    !cityQuery ? "is-empty" : ""
                  }`}
                  value={cityQuery}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setCityQuery(nextValue);
                    setBasedIn(resolveExactOptionMatch(cityOptions, nextValue));
                    setIsCityOpen(true);
                    setIsYearOpen(false);
                  }}
                  onFocus={() => {
                    setIsCityOpen(true);
                    setIsYearOpen(false);
                  }}
                  placeholder="Select your city"
                />
                <button
                  type="button"
                  className={`create-business-profile-dropdown-caret-btn ${
                    isCityOpen ? "is-open" : ""
                  }`}
                  onClick={() => {
                    setIsCityOpen((prev) => !prev);
                    setIsYearOpen(false);
                    if (isCityOpen) {
                      setCityQuery(basedIn || "");
                    }
                  }}
                  aria-label={isCityOpen ? "Close city options" : "Open city options"}
                >
                  <img
                    src="/CaretRight.svg"
                    alt=""
                    className="create-business-profile-dropdown-caret"
                  />
                </button>
              </div>
              {isCityOpen ? (
                <div className="create-business-profile-dropdown-list">
                  {filteredCityOptions.length > 0 ? (
                    filteredCityOptions.map((city) => (
                      <button
                        key={city}
                        type="button"
                        className={`create-business-profile-dropdown-option ${
                          basedIn === city ? "is-selected" : ""
                        }`}
                        onClick={() => {
                          setBasedIn(city);
                          setCityQuery(city);
                          setIsCityOpen(false);
                        }}
                      >
                        {city}
                      </button>
                    ))
                  ) : (
                    <div className="create-business-profile-dropdown-empty">
                      No cities found
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </main>

        <footer className="create-business-profile-footer">
          <div className="create-business-profile-progress-track">
            <div
              className="create-business-profile-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            type="button"
            className="create-business-profile-continue-btn"
            data-analytics-lead-submit
            disabled={!canContinue || isContinuing}
            onClick={handleCreateUser}
          >
            {isContinuing ? "Continuing..." : <>Continue <span aria-hidden="true">&#8250;</span></>}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CreateBusinessProfilePage;
